// @ts-ignore
import _ from "lodash";
import moment from "moment";
import { useContext, useEffect } from "react";
import { useCompanyConfigQuery } from "../../../../../SWR/Company/useCompanyConfigQuery";
import { CustomizationContext, CustomizationModalStateContext, } from "../../../../../Shared/components/CustomizeModal/CustomizationContext";
import { usePriceFormat } from "../../../../../Shared/hooks/usePriceFormat/usePriceFormat";
import { tokenReplace } from "../../../../../Shared/utils/tokenReplacement";
import { useRecipeSelection } from "../../../../hooks/RecipeSelection/useRecipeSelection";
import { getDayFilterDays } from "../../../../hooks/useHelpers";
import { ActionTypes } from "../../../../OrderFlowProvider/OrderFlowActionTypes";
import { useOrderFlow } from "../../../../OrderFlowProvider/OrderFlowContext";
import { ModuleNames, RecipeIngredientReplacementMode, } from "../../../../OrderFlowProvider/OrderFlowTypes";
import { RecipeFilterContext } from "../../RecipeFilterModule/RecipeFilterContext";
import { isAnyMealSkippedForGivenDay } from "../../DayFilter/useDayFilter";
import { RecipeModuleText } from "../utils/RecipeModule-Types";
import { getProgramMealsWithRecipes, reconcileMeals, restoreMeals as buildRestoreMeals } from "../utils/RecipeModule-utils";
import { useRecipeModuleConfig } from "./useRecipeModuleConfig";
/**
 * Custom hook for managing the recipe module logic within the order flow.
 *
 * Handles fetching recipes, managing meal assignments, and providing configuration
 * and state for recipe selection and display. Integrates with various contexts and
 * utilities to support features such as auto-selection, filtering, logging, and UI state.
 *
 * @param sequenceNumber - The sequence number identifying the module instance in the flow.
 * @returns
 *
 * @remarks
 * This hook centralizes logic and state for recipe selection and assignment,
 * ensuring consistency and reducing duplication across components using the recipe module.
 */
export const useRecipeModule = (sequenceNumber) => {
    var _a;
    const { store, dispatch } = useOrderFlow();
    const { formatPrice } = usePriceFormat();
    const { getRecipes, getMealsWithSelectionSource } = useRecipeSelection();
    const { tokenMap } = useCompanyConfigQuery();
    const { meals, assignmentId, setAssignmentId } = useContext(CustomizationContext);
    const { isCustomizeModalOpen, setIsCustomizeModalOpen } = useContext(CustomizationModalStateContext);
    const { filterState } = useContext(RecipeFilterContext);
    const { enableAddMeals, enableAssignRecipes, enableRemoveMeals, moduleConfig, singleRecipeSelectLimit } = useRecipeModuleConfig(sequenceNumber);
    const dayFilterForCurrentPage = (_a = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.DAY_FILTER && module.Page === store.cart.currentPage)) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    useEffect(() => {
        var _a, _b, _c;
        const dayFilterDays = getDayFilterDays(store.cart.daysOfWeek, store.model.Recipes, store.model.DeliveryDays, store.cart.startDate, store.cart.isSubscription, store.cart.subscriptionPeriod, store.cart.oneTimeOrderPeriod, dayFilterForCurrentPage === null || dayFilterForCurrentPage === void 0 ? void 0 : dayFilterForCurrentPage.OnlySelectedDaysOfWeek, 
        // Include all possible days, regardless of whether a menu is set, to ensure no days are excluded.
        false, store.model.GeneralSettings.OnlyShowDeliveryDays, 
        // Default to first available day if the Day Filter Module isn't present
        (_a = dayFilterForCurrentPage === null || dayFilterForCurrentPage === void 0 ? void 0 : dayFilterForCurrentPage.DayCount) !== null && _a !== void 0 ? _a : 1);
        const flowContainsMealQuantityModule = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.MEAL_QUANTITY);
        const flowContainsMealBundleChooser = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.MEAL_BUNDLE_CHOOSER);
        const recipeFetchFlags = {
            shouldAutoSelectRecipes: moduleConfig.RecipeAutoSelection,
            shouldReplaceIngredients: store.model.OrderFlowSettings.RecipeIngredientReplacementMode === RecipeIngredientReplacementMode.ENABLED,
        };
        if (!store.model.RecipeFetchStatus.completed) {
            getRecipes(recipeFetchFlags, store.cart.meals, {
                ProgramSKU: store.model.Program.Sku,
                RecipeTypeId: (_c = (_b = store.cart.recipeType) === null || _b === void 0 ? void 0 : _b.recipeTypeId) !== null && _c !== void 0 ? _c : 0,
                Meals: getMealsWithSelectionSource(store.cart.meals, store.model.Program.ProgramMeals, store.cart.mealSize, store.model.allMealSizeOption, moduleConfig.RecipeSelectionType, !flowContainsMealQuantityModule || !flowContainsMealBundleChooser, moduleConfig === null || moduleConfig === void 0 ? void 0 : moduleConfig.ProgramMeals, dayFilterDays, store.model.ExplodeOMDD, moduleConfig.RecipeAutoSelection, store.model.GeneralSettings.OnlyShowDeliveryDays),
                Preferences: store.cart.omissions.map((omission) => {
                    return {
                        CategoryName: omission.categoryName,
                        IngredientMajorID: omission.dislikeId,
                        IngredientMajorName: omission.dislikeName,
                    };
                }),
                AssignConflictingRecipes: recipeFetchFlags.shouldReplaceIngredients,
            });
        }
    }, [store.model.RecipeFetchStatus.completed]);
    // ensure that we haven't assigned more meals than allowed
    useEffect(() => {
        const reconciledMeals = reconcileMeals(store.cart.meals, enableAddMeals);
        if (!_.isEqual(reconcileMeals, store.cart.meals)) {
            dispatch({ type: ActionTypes.SET_MEALS, payload: reconciledMeals });
        }
    }, [enableAddMeals]);
    const recipeSelectionType = moduleConfig === null || moduleConfig === void 0 ? void 0 : moduleConfig.RecipeSelectionType;
    const noMealsText = tokenReplace(RecipeModuleText.NoMealsForDayText, false, tokenMap);
    // if recipes are displayed in a grid and arent grouped by meal (and recipe assignment is disabled),
    // aggregate all ItemCards into one ItemCardSlider
    const renderAllRecipesSlider = moduleConfig.IsGridLayout && !enableAssignRecipes && !moduleConfig.GroupByMeal;
    const programMealsWithRecipes = getProgramMealsWithRecipes(store, moduleConfig.ProgramMeals, filterState, moduleConfig, enableAssignRecipes, enableAddMeals, enableRemoveMeals, formatPrice, isCustomizeModalOpen, setIsCustomizeModalOpen, recipeSelectionType);
    const showNoMealsText = !programMealsWithRecipes || !programMealsWithRecipes.length;
    const isAnyMealSkippedForDay = isAnyMealSkippedForGivenDay(store.cart.meals)(moment(store.cart.selectedDay));
    const restoreMeals = buildRestoreMeals(dispatch, store.cart.meals, moment(store.cart.selectedDay));
    // Consolidated props object for all curried handler functions
    // This centralizes the common parameters used across multiple handlers
    const recipeHandlerProps_One = {
        // Core enablement flags
        enableAssignRecipes,
        enableAddMeals,
        enableRemoveMeals,
        // Store data
        cartMeals: store.cart.meals,
        recipes: store.model.Recipes,
        program: store.model.Program,
        deliveryDays: store.model.DeliveryDays,
        sku: store.model.Sku,
        template: store.cart.template,
        daysOfWeek: store.cart.daysOfWeek,
        selectedDay: store.cart.selectedDay,
        onlyShowDeliveryDays: store.model.GeneralSettings.OnlyShowDeliveryDays,
        // Context data
        customizationMeals: meals,
        assignmentId,
        setAssignmentId,
        // Dispatch and logging
        dispatch,
        // Toggle-specific props
        singleRecipeSelectLimit,
    };
    return {
        // Export the consolidated props for potential optimization or debugging
        recipeHandlerProps_One,
        isAnyMealSkippedForDay,
        mealSelectionStatus: store.model.MealSelectionStatus,
        moduleConfig,
        noMealsText,
        programMealsWithRecipes,
        recipeAutoSelection: moduleConfig.RecipeAutoSelection,
        recipeFetchStatus: store.model.RecipeFetchStatus,
        renderAllRecipesSlider,
        restoreMeals,
        selectedDay: store.cart.selectedDay,
        showNoMealsText,
    };
};
